/**
 * A miniatura de um arquivo do Desk Cloud (docs/spec/desk-cloud.md): a imagem (ou a miniatura dela), o
 * token num círculo, o primeiro quadro do vídeo, o áudio com o play e o resto com o ícone e a extensão.
 * Serve o cartão e a linha da página do Desk Cloud e o seletor de arquivos (o clipe do chat, o "Todo o
 * Desk Cloud" do painel Arquivos).
 */
import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n/index.js';
import { serverUrl } from '../serverUrl.js';
import type { DeskFile } from '../shared.js';
import { Icon } from '../ui/icons.js';
import { durationText, splitName } from './cloudModel.js';
import { useListen } from './listen.js';

export function FileThumb({ file, size }: { file: DeskFile; size: 'card' | 'row' }) {
  const t = useT();
  const listen = useListen();
  const [broken, setBroken] = useState(false);
  const { ext } = splitName(file.name);
  const image = file.mime.startsWith('image/') && !broken;
  const video = file.mime.startsWith('video/') && !file.thumbUrl && !broken;
  const src = file.thumbUrl ?? (image ? file.url : null);
  const playing = listen.id === file.id && listen.playing;
  return (
    <span className={`rd-cloudThumb rd-cloudThumb--${size} rd-cloudThumb--${file.kind}${image ? ' has-image' : ''}${video ? ' has-video' : ''}`}>
      {(image || file.thumbUrl) && src && !broken ? (
        <img src={serverUrl(src)} alt="" loading="lazy" draggable={false} onError={() => setBroken(true)} />
      ) : video ? (
        <VideoFrame src={serverUrl(file.url)} size={size} onError={() => setBroken(true)} />
      ) : file.kind === 'audio' ? (
        <span className="rd-cloudThumb__audio" aria-hidden="true">
          <Icon name="music" size={size === 'card' ? 30 : 20} strokeWidth={1.6} />
          {size === 'card' && file.durationMs !== null ? <span className="rd-cloudThumb__duration">{durationText(file.durationMs)}</span> : null}
          {listen.id === file.id ? (
            <span className="rd-cloudThumb__progress">
              <span style={{ width: `${listen.progress * 100}%` }} />
            </span>
          ) : null}
        </span>
      ) : (
        <span className="rd-cloudThumb__file" aria-hidden="true">
          <Icon name={file.kind === 'doc' ? 'sheet' : 'file'} size={size === 'card' ? 30 : 20} strokeWidth={1.5} />
          {size === 'card' && ext ? <span className="rd-cloudThumb__ext">{ext}</span> : null}
        </span>
      )}
      {file.kind === 'audio' && file.trashedAt === null ? (
        <button
          type="button"
          className={`rd-cloudThumb__play${playing ? ' is-playing' : ''}`}
          aria-label={t(playing ? 'cloud.pause' : 'cloud.listen', { name: file.name })}
          onClick={(e) => {
            e.stopPropagation();
            listen.toggle(file);
          }}
        >
          <Icon name={playing ? 'pause' : 'play'} size={size === 'card' ? 16 : 14} filled />
        </button>
      ) : null}
    </span>
  );
}

/**
 * O primeiro quadro do vídeo, pedido só quando a miniatura chega perto da tela: cada vídeo lê o começo
 * do arquivo. A duração vem dos metadados, quando o arquivo a tem (o WebM gravado pelo navegador não
 * tem). Sem o vídeo (formato que o navegador não abre), fica o ícone.
 */
function VideoFrame({ src, size, onError }: { src: string; size: 'card' | 'row'; onError: () => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');
  const [duration, setDuration] = useState<number | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || near) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setNear(true);
        observer.disconnect();
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [near]);
  return (
    <>
      <video
        ref={ref}
        className="rd-cloudThumb__video"
        // o #t= faz o navegador mostrar o quadro (sem ele, alguns deixam o vídeo preto até tocar)
        src={near ? `${src}#t=0.1` : undefined}
        preload="metadata"
        muted
        playsInline
        disablePictureInPicture
        tabIndex={-1}
        aria-hidden="true"
        onLoadedMetadata={(e) => {
          const seconds = e.currentTarget.duration;
          if (Number.isFinite(seconds)) setDuration(seconds * 1000);
        }}
        onError={onError}
      />
      <span className="rd-cloudThumb__videoMark" aria-hidden="true">
        <Icon name="play" size={size === 'card' ? 14 : 9} filled />
      </span>
      {size === 'card' && duration !== null ? <span className="rd-cloudThumb__videoTime">{durationText(duration)}</span> : null}
    </>
  );
}
